import { getQuestionTypeLabel } from './assessmentResultUtils';
import { LYW_BRAND } from '../../constants/brandPalette';
import { formatDisplayDate } from '../../../utils/formatDisplayDate';

function escapeHtml(text: string): string {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function pickString(obj: Record<string, unknown> | null | undefined, ...keys: string[]): string {
    if (!obj) return '';
    for (const key of keys) {
        const value = obj[key];
        if (value != null && String(value).trim()) return String(value).trim();
    }
    return '';
}

function pickArray<T>(obj: Record<string, unknown> | null | undefined, ...keys: string[]): T[] {
    if (!obj) return [];
    for (const key of keys) {
        const value = obj[key];
        if (Array.isArray(value)) return value as T[];
    }
    return [];
}

function normalizeQuestionId(id: string): string {
    return id.replace(/-/g, '').toLowerCase();
}

function matchResultQuestion(
    resultQuestions: Record<string, unknown>[],
    previewQuestion: Record<string, unknown>,
    questionIndex: number
): Record<string, unknown> | undefined {
    const previewId = pickString(previewQuestion, 'e154_QuestionId', 'E154_QuestionId');
    if (previewId) {
        const normalizedPreview = normalizeQuestionId(previewId);
        const byId = resultQuestions.find((rq) => {
            const resultId = pickString(rq, 'e177_QuestionId', 'E177_QuestionId');
            return resultId && normalizeQuestionId(resultId) === normalizedPreview;
        });
        if (byId) return byId;
    }
    return resultQuestions[questionIndex];
}

function formatAnswerOptions(
    question: Record<string, unknown>,
    includeCorrectAnswers: boolean
): string {
    const data = (question.e154_QuestionData ?? question.E154_QuestionData) as Record<string, unknown> | undefined;
    if (!data) return '';

    const possibleAnswers = pickArray<Record<string, unknown>>(data, 'e154_PossibleAnswers', 'E154_PossibleAnswers');
    if (possibleAnswers.length === 0) return '';

    const correctKeys = pickArray<string>(data, 'e154_CorrectAnswerKeys', 'E154_CorrectAnswerKeys');
    const correctKey = pickString(data, 'e154_CorrectAnswerKey', 'E154_CorrectAnswerKey');

    const items = possibleAnswers
        .map((answer, index) => {
            const enumeration = (answer.e154_Enumeration ?? answer.E154_Enumeration) as
                | Record<string, unknown>
                | undefined;
            const key = pickString(enumeration ?? {}, 'key', 'Key');
            const value = pickString(enumeration ?? {}, 'value', 'Value') || 'No value';
            const letter = String.fromCharCode(65 + index);
            const isCorrect =
                includeCorrectAnswers &&
                (correctKeys.includes(key) || (correctKey && correctKey === key));
            return `<li><strong>${letter}.</strong> ${escapeHtml(value)}${
                isCorrect ? ' <span class="correct-mark">(correct)</span>' : ''
            }</li>`;
        })
        .join('');

    return `<ul class="options-list">${items}</ul>`;
}

function buildQuestionBlocks(
    assessment: Record<string, unknown>,
    result: Record<string, unknown> | null | undefined
): string {
    const includeResults = !!result;
    const resultQuestions = includeResults
        ? pickArray<Record<string, unknown>>(result!, 'e177_Questions', 'E177_Questions')
        : [];

    const sections = pickArray<Record<string, unknown>>(assessment, 'e154_Sections', 'E154_Sections');
    if (sections.length === 0 && resultQuestions.length > 0) {
        return resultQuestions
            .map((q, index) => buildResultQuestionBlock(q, index, includeResults))
            .join('');
    }

    let questionNumber = 0;
    return sections
        .map((section, sectionIndex) => {
            const sectionTitle =
                pickString(section, 'e154_SectionTitle', 'E154_SectionTitle') ||
                `Section ${sectionIndex + 1}`;
            const questions = pickArray<Record<string, unknown>>(
                section,
                'e154_Questions',
                'E154_Questions'
            );

            const questionHtml = questions
                .map((question) => {
                    const questionIndex = questionNumber;
                    questionNumber += 1;
                    const matchedResult = includeResults
                        ? matchResultQuestion(resultQuestions, question, questionIndex)
                        : undefined;
                    return buildQuestionBlock(
                        question,
                        questionNumber,
                        matchedResult,
                        includeResults
                    );
                })
                .join('');

            return `
                <div class="section">
                    <h2>${escapeHtml(sectionTitle)}</h2>
                    ${questionHtml || '<p class="no-content">No questions in this section.</p>'}
                </div>
            `;
        })
        .join('');
}

function buildQuestionBlock(
    question: Record<string, unknown>,
    questionNumber: number,
    resultQuestion: Record<string, unknown> | undefined,
    includeResults: boolean
): string {
    const questionText =
        pickString(question, 'e154_QuestionText', 'E154_QuestionText') || 'Untitled question';
    const questionTypeKey = pickString(question, 'e154_QuestionTypeKey', 'E154_QuestionTypeKey');
    const points = question.e154_QuestionPoints ?? question.E154_QuestionPoints ?? 0;
    const optionsHtml = formatAnswerOptions(question, includeResults);

    const resultBlock =
        includeResults
            ? resultQuestion
                ? buildResultDetailsBlock(resultQuestion)
                : ''
            : `<div class="answer-blank"><strong>Your answer:</strong></div>`;

    return `
        <div class="question">
            <div class="question-header">
                <h3>Question ${questionNumber}: ${escapeHtml(questionText)}</h3>
                <span class="question-meta">${escapeHtml(getQuestionTypeLabel(questionTypeKey))} · ${points} pts</span>
            </div>
            ${optionsHtml}
            ${resultBlock}
        </div>
    `;
}

function buildResultQuestionBlock(
    resultQuestion: Record<string, unknown>,
    index: number,
    includeResults: boolean
): string {
    const questionText =
        pickString(resultQuestion, 'e177_QuestionText', 'E177_QuestionText') || 'Untitled question';
    const questionTypeKey = pickString(
        resultQuestion,
        'e177_QuestionTypeKey',
        'E177_QuestionTypeKey'
    );
    const maxPoints =
        resultQuestion.e177_PointsPossible ??
        resultQuestion.e177_MaxPoints ??
        resultQuestion.E177_PointsPossible ??
        0;

    return `
        <div class="question">
            <div class="question-header">
                <h3>Question ${index + 1}: ${escapeHtml(questionText)}</h3>
                <span class="question-meta">${escapeHtml(getQuestionTypeLabel(questionTypeKey))} · ${maxPoints} pts</span>
            </div>
            ${includeResults ? buildResultDetailsBlock(resultQuestion) : ''}
        </div>
    `;
}

function buildResultDetailsBlock(resultQuestion: Record<string, unknown>): string {
    const studentAnswer =
        pickString(resultQuestion, 'e177_StudentAnswer', 'E177_StudentAnswer') ||
        'No answer provided';
    const correctAnswer =
        pickString(resultQuestion, 'e177_CorrectAnswer', 'E177_CorrectAnswer') || 'N/A';
    const pointsGiven = resultQuestion.e177_PointsGiven ?? resultQuestion.E177_PointsGiven ?? 0;
    const maxPoints =
        resultQuestion.e177_PointsPossible ??
        resultQuestion.e177_MaxPoints ??
        resultQuestion.E177_PointsPossible ??
        0;
    const wasSkipped = resultQuestion.e177_WasSkipped === true || resultQuestion.E177_WasSkipped === true;

    return `
        <div class="result-details">
            <p><strong>Student answer:</strong> ${escapeHtml(studentAnswer)}</p>
            <p><strong>Correct answer:</strong> ${escapeHtml(correctAnswer)}</p>
            <p><strong>Points:</strong> ${pointsGiven} / ${maxPoints}${wasSkipped ? ' (skipped)' : ''}</p>
        </div>
    `;
}

export interface PrintAssessmentDocumentOptions {
    title: string;
    assessment: Record<string, unknown>;
    result?: Record<string, unknown> | null;
    statusLabel?: string | null;
    completedAt?: string | null;
    scorePercent?: number | null;
}

export function buildAssessmentPrintHtml({
    title,
    assessment,
    result,
    statusLabel,
    completedAt,
    scorePercent,
}: PrintAssessmentDocumentOptions): string {
    const logoUrl = `${window.location.origin}/z30-images/logo_lyw_full_400x200.png`;
    const instructions = pickString(assessment, 'e154_Instructions', 'E154_Instructions');
    const description = pickString(assessment, 'e154_Description', 'E154_Description');
    const includeResults = !!result;
    const score =
        scorePercent ??
        (result?.e177_StudentScoreAsPercentage as number | undefined) ??
        (result?.E177_StudentScoreAsPercentage as number | undefined) ??
        null;

    const completedLine =
        completedAt && !Number.isNaN(new Date(completedAt).getTime())
            ? `<p><strong>Completed:</strong> ${escapeHtml(formatDisplayDate(completedAt))}</p>`
            : '';

    return `
        <!DOCTYPE html>
        <html>
        <head>
            <title>${escapeHtml(title)}</title>
            <style>
                @media print {
                    @page { margin: 1in; }
                }
                body {
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                    line-height: 1.6;
                    color: #333;
                    max-width: 800px;
                    margin: 0 auto;
                    padding: 20px;
                }
                h1 { color: #1a1a1a; margin: 0; }
                h2 {
                    color: #374151;
                    margin-top: 28px;
                    margin-bottom: 12px;
                    font-size: 1.15em;
                    border-bottom: 1px solid #e5e7eb;
                    padding-bottom: 6px;
                }
                h3 { color: #111827; font-size: 1em; margin: 0 0 8px; }
                .header-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 20px;
                    gap: 16px;
                }
                .header-logo { height: 56px; width: auto; }
                .assessment-info {
                    background: #f9fafb;
                    padding: 14px 16px;
                    border-radius: 8px;
                    margin-bottom: 20px;
                    border-left: 4px solid ${LYW_BRAND.actionBlue};
                }
                .assessment-info p { margin: 4px 0; }
                .section { margin-bottom: 24px; }
                .question {
                    margin-bottom: 20px;
                    padding: 14px 16px;
                    border: 1px solid #e5e7eb;
                    border-radius: 6px;
                    border-left: 4px solid #3b82f6;
                }
                .question-header { margin-bottom: 10px; }
                .question-meta {
                    display: block;
                    font-size: 0.85em;
                    color: #6b7280;
                    margin-top: 4px;
                }
                .options-list { margin: 8px 0 12px 20px; padding: 0; }
                .options-list li { margin-bottom: 4px; }
                .correct-mark { color: ${LYW_BRAND.confirmActionDark}; font-weight: 600; }
                .answer-blank {
                    margin-top: 12px;
                    min-height: 48px;
                    border-bottom: 1px solid #9ca3af;
                }
                .result-details {
                    margin-top: 12px;
                    padding: 10px 12px;
                    background: ${LYW_BRAND.confirmActionTint};
                    border-radius: 4px;
                    font-size: 0.95em;
                }
                .result-details p { margin: 6px 0; }
                .instructions, .description {
                    margin-bottom: 20px;
                    padding: 12px 14px;
                    background: #fff;
                    border: 1px solid #e5e7eb;
                    border-radius: 4px;
                }
                .no-content { color: #9ca3af; font-style: italic; }
                .score-banner {
                    font-size: 1.25em;
                    font-weight: 700;
                    color: #166534;
                    margin: 8px 0 0;
                }
            </style>
        </head>
        <body>
            <div class="header-row">
                <h1>${escapeHtml(title)}</h1>
                <img src="${logoUrl}" alt="Learn Your Way Logo" class="header-logo" onerror="this.style.display='none'">
            </div>

            <div class="assessment-info">
                <p><strong>Document:</strong> ${includeResults ? 'Assessment with results' : 'Assessment (student copy)'}</p>
                ${statusLabel ? `<p><strong>Status:</strong> ${escapeHtml(statusLabel)}</p>` : ''}
                ${score != null ? `<p class="score-banner">Score: ${score}%</p>` : ''}
                ${completedLine}
            </div>

            ${instructions ? `<div class="instructions"><h2>Instructions</h2>${instructions}</div>` : ''}
            ${description ? `<div class="description"><h2>Description</h2><p>${escapeHtml(description)}</p></div>` : ''}

            ${buildQuestionBlocks(assessment, result ?? null)}

            ${!pickArray(assessment, 'e154_Sections', 'E154_Sections').length && !result ? '<p class="no-content">No assessment content available.</p>' : ''}
        </body>
        </html>
    `;
}

export function openAssessmentPrintWindow(html: string): void {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
        printWindow.print();
    }, 250);
}
